// Shared plumbing for the in-page embed elements: shadow-root stylesheet
// injection and host-URL parameter reading (cfb-booking pattern — the widget
// shares the host document, so deep links arrive on the host page's own URL).

/**
 * Every fetch an embed makes before it has UI on screen must carry a hard
 * timeout: a stalled connection would otherwise leave the element blank
 * forever with no error state ever rendering. 15s absorbs a Cloud Run cold
 * start; a stall is longer than that.
 */
const EMBED_FETCH_TIMEOUT_MS = 15_000;

// Production bundles carry their matching stylesheet so separately cached
// JS and CSS cannot leave a live booking flow unstyled after a deployment.
// Undefined in source-level tests/dev builds, which retain the fetch fallback.
declare const __EMBED_STYLESHEETS__: Record<string, string> | undefined;

export function fetchWithTimeout(url: string): Promise<Response> {
  const signal =
    typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
      ? AbortSignal.timeout(EMBED_FETCH_TIMEOUT_MS)
      : undefined;
  return fetch(url, signal ? { signal } : undefined);
}

/** Fetch + cache the bundle stylesheet once per (baseUrl, file) pair. */
const cssCache = new Map<string, Promise<string>>();

export function loadStylesheet(baseUrl: string, file: string): Promise<string> {
  if (typeof __EMBED_STYLESHEETS__ !== 'undefined' && __EMBED_STYLESHEETS__?.[file]) {
    return Promise.resolve(__EMBED_STYLESHEETS__[file]);
  }
  const url = `${baseUrl.replace(/\/$/, '')}/embed/${file}`;
  let p = cssCache.get(url);
  if (!p) {
    p = fetchWithTimeout(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .catch((err) => {
        cssCache.delete(url);
        // Unstyled beats broken: log and render with base styles only.
        console.warn(`[clinic-widget] failed to load ${file}:`, err);
        return '';
      });
    cssCache.set(url, p);
  }
  return p;
}

/**
 * Inject the widget stylesheet plus a `:host` baseline into a shadow root.
 * The stylesheet itself is authored against `:root, :host, .booking-widget`
 * so it applies unchanged inside the shadow tree.
 *
 * The trailing override strips the `.booking-widget` SHELL chrome — the
 * hosted /[locale]/booking page is a full viewport (100dvh min-height, brand
 * mesh background, page-level padding), but an embed is a bare form body:
 * the HOST page owns background and spacing, so the shell must sit flush and
 * transparent inside it. Appended after the fetched CSS so it wins the
 * cascade at equal specificity.
 */
const EMBED_SHELL_OVERRIDES =
  '.booking-widget{min-height:0;padding:0;background:none}';

/**
 * Rewrite every width-based media query to the equivalent container query.
 *
 * The stylesheet is authored for the hosted booking page, where the widget IS
 * the page — so its responsive rules are viewport media queries. An embed is
 * not the page: it can sit in a 480px column of a 1500px-wide desktop
 * viewport (lp.fabricadosorriso.pt's hero does exactly this) and a viewport
 * query then renders the desktop layout squeezed into a phone's width —
 * truncated professional cards, a stepper with no room, a chip the 560px rule
 * meant to hide. The embed host is a size container (see injectStyles) and
 * the breakpoints keep their values, measured against the element instead of
 * the window. Non-width queries (prefers-reduced-motion) pass through.
 *
 * `useIsMobilePane` makes the same viewport→element move on the JS side —
 * the two must stay in agreement or the slot step's Back button lies.
 */
export function containerizeWidthQueries(css: string): string {
  return css.replace(/@media\s*\(\s*(min|max)-width\s*:/g, '@container ($1-width:');
}

export async function injectStyles(shadow: ShadowRoot, baseUrl: string, file: string): Promise<void> {
  const css = await loadStylesheet(baseUrl, file);
  const style = document.createElement('style');
  style.textContent = `:host{display:block;width:100%;container-type:inline-size}\n${containerizeWidthQueries(css)}\n${EMBED_SHELL_OVERRIDES}`;
  shadow.prepend(style);
}

export interface HostParams {
  handoffToken?: string;
  clinic?: string;
  service?: string;
  doctor?: string;
  slot?: string;
  online?: boolean;
}

/**
 * Read deep-link params from the HOST page URL. `?clinica=` is accepted as an
 * alias of `?clinic=` (fsd-website's historical param). Explicit element
 * attributes always take precedence over these.
 */
export function readHostParams(): HostParams {
  try {
    const q = new URLSearchParams(window.location.search);
    const pick = (k: string) => {
      const v = q.get(k);
      return v && v.trim() !== '' ? v.trim() : undefined;
    };
    const online = pick('online');
    return {
      handoffToken: pick('h'),
      clinic: pick('clinic') ?? pick('clinica'),
      service: pick('service'),
      doctor: pick('doctor'),
      slot: pick('slot'),
      online: online === '1' || online === 'true' ? true : undefined,
    };
  } catch {
    return {};
  }
}

/**
 * Best-effort deprecation telemetry (§3.6): POSTs to /api/public/widget-compat
 * so ops can measure how much legacy `<cfb-booking-widget>` / `__resolve__`
 * traffic still exists before the compat paths are deleted. sendBeacon
 * survives page unload and never blocks the widget; absent (old browsers),
 * this is a no-op — losing a data point beats risking the embed.
 */
export function sendCompatBeacon(
  baseUrl: string,
  siteId: string,
  feature: 'cfb-element' | 'raw-query',
): void {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
    navigator.sendBeacon(
      `${baseUrl.replace(/\/$/, '')}/api/public/widget-compat`,
      JSON.stringify({ siteId, feature }),
    );
  } catch {
    // telemetry must never break the widget
  }
}

/** Dispatch a composed CustomEvent from the element so host pages can listen. */
export function dispatchHostEvent(el: HTMLElement, name: string, detail?: unknown): void {
  try {
    el.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  } catch {
    // a throwing host listener must not break the widget
  }
}
